import {
  Component,
  contentChild,
  effect,
  ChangeDetectionStrategy,
  computed,
  input,
} from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { InputDirective } from '@fc-shared/ui/input/core/input.directive';
import { generateRandomString } from '@fc-shared/utils/data.utils';
import { PrefixDirective } from '@fc-shared/ui/input/core/prefix.directive';
import { SuffixDirective } from '@fc-shared/ui/input/core/suffix.directive';
import { LabelComponent } from '@fc-shared/ui/input/core/label/label.component';
import { ErrorComponent } from '@fc-core/form-utils/form-error/error.component';
import { HintComponent } from '@fc-shared/ui/input/core/hint/hint.component';
import {
  resolveErrorMessage,
  SignalFormErrorMessages,
} from '@fc-core/form-utils/signal-form-errors';

@Component({
  selector: 'fc-form-field',
  standalone: true,
  imports: [ErrorComponent],
  template: `
    <div class="fc-form-field">
      <label class="fc-form-field-label" [for]="id">
        <ng-content select="fc-label"></ng-content>
      </label>
      <div class="fc-form-filed-input">
        <ng-content select="[fcInput]"></ng-content>
        <div class="error-level">
          <ng-content select="fc-error"></ng-content>
          @if (!error() && shouldShowSignalFieldError()) {
            <fc-error>{{ signalFieldErrorMessage() }}</fc-error>
          }
        </div>
        <div class="hints-level">
          <ng-content select="fc-hint"></ng-content>
        </div>
        <div class="fc-form-field-prefix">
          <ng-content select="[fcPrefix]"></ng-content>
        </div>
        <div class="fc-form-field-suffix">
          <ng-content select="[fcSuffix]"></ng-content>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.fc-form-field--has-prefix]': '!!prefix()',
    '[class.fc-form-field--has-suffix]': '!!suffix()',
    '[class.fc-form-field--has-label]': '!!label()',
    '[class.fc-form-field--has-error]':
      '!!error() || shouldShowSignalFieldError()',
    '[class.fc-form-field--show-error]': 'shouldShowSignalFieldError()',
    '[class.fc-form-field--has-hint]': '!!hint()',
    '[class.fc-form-field--required]': 'isRequired()',
  },
})
export class FormFieldComponent {
  id = generateRandomString(4);
  errorMessages = input<SignalFormErrorMessages>({});

  label = contentChild(LabelComponent);
  input = contentChild(InputDirective);
  signalFormField = contentChild(FormField);
  error = contentChild(ErrorComponent);
  prefix = contentChild(PrefixDirective);
  suffix = contentChild(SuffixDirective);
  hint = contentChild(HintComponent);

  isRequired = computed(
    () =>
      (this.input()?.isRequired() ?? false) ||
      (this.signalFormField()?.state().required() ?? false),
  );

  shouldShowSignalFieldError = computed(() => {
    const fieldState = this.signalFormField()?.state();
    return !!fieldState && fieldState.touched() && fieldState.invalid();
  });

  signalFieldErrorMessage = computed(() =>
    resolveErrorMessage(
      this.signalFormField()?.errors()[0],
      this.errorMessages(),
    ),
  );

  constructor() {
    effect(() => {
      const inputEl = this.input();
      if (inputEl) {
        inputEl.elRef.nativeElement.id = this.id;
      }
    });
  }
}
